import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { authApi } from "@/api/endpoints";
import { apiUrl, errorMessage } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { RETURN_TO_KEY } from "@/auth/guards";
import { Button } from "@/components/ui/Button";
import { TurnstileWidget } from "@/components/Turnstile";
import { getConfig } from "@/config";

function DiscordMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden fill="currentColor">
      <path d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.3.5a18.3 18.3 0 0 1 4.4 1.4 17.4 17.4 0 0 0-15 0A18.3 18.3 0 0 1 8.9 3.5L8.6 3a19.8 19.8 0 0 0-4.9 1.4C.6 9 0 13.5.3 18a19.9 19.9 0 0 0 6 3l1.3-2.1a12.9 12.9 0 0 1-2-1l.5-.4a14.2 14.2 0 0 0 11.9 0l.5.4c-.6.4-1.3.7-2 1l1.3 2.1a19.9 19.9 0 0 0 6-3c.4-5.2-.7-9.7-3.5-13.6ZM8.5 15.3c-1.2 0-2.2-1.1-2.2-2.4s1-2.4 2.2-2.4 2.2 1.1 2.2 2.4-1 2.4-2.2 2.4Zm7 0c-1.2 0-2.2-1.1-2.2-2.4s1-2.4 2.2-2.4 2.2 1.1 2.2 2.4-1 2.4-2.2 2.4Z" />
    </svg>
  );
}

export function safeReturnTo(raw: string | null): string {
  // Only same-app paths: never let ?next= send a user to another origin.
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/auth/")) return "/";
  return raw;
}

export function LoginPage() {
  const { tokens, signIn, expired } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [devBusy, setDevBusy] = useState(false);
  const cfg = getConfig();
  const next = safeReturnTo(params.get("next"));
  const needsCaptcha = cfg.turnstileSiteKey !== "";
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const turnstileRefused = params.get("error") === "turnstile";
  const loginHref = apiUrl(
    needsCaptcha && captcha ? `${cfg.discordLoginPath}?turnstile=${encodeURIComponent(captcha)}` : cfg.discordLoginPath,
  );
  const loginReady = !needsCaptcha || captcha !== null;

  if (tokens) return <Navigate to={next} replace />;

  const rememberReturn = () => {
    try {
      sessionStorage.setItem(RETURN_TO_KEY, next);
    } catch {
      // fall back to "/" after login
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4">
      <div className="w-full max-w-[312px]">
        <div className="mb-4 flex items-center gap-2.5">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-wash text-accent-text">
            <ShieldCheck className="size-4" aria-hidden />
          </div>
          <div>
            <h1 className="text-[15px] leading-tight font-semibold">{cfg.appName}</h1>
            <p className="text-xs text-ink-3">Bảng điều khiển license - tường lửa - antiddos</p>
          </div>
        </div>
        <div className="rounded-xl border border-line bg-surface p-4 shadow-soft">
          {expired && (
            <p className="mb-3 rounded-md bg-warning-wash px-3 py-2 text-xs text-ink" role="status">
              Phiên đăng nhập đã hết hạn. Hãy đăng nhập lại.
            </p>
          )}
          {turnstileRefused && (
            <p className="mb-3 rounded-md bg-critical-wash px-3 py-2 text-xs text-ink" role="alert">
              Xác minh chống bot không hợp lệ hoặc đã hết hạn. Hãy xác minh lại rồi đăng nhập.
            </p>
          )}
          <p className="mb-3 text-[13px] text-ink-2">Đăng nhập bằng tài khoản Discord đã được cấp quyền quản trị.</p>
          {needsCaptcha && (
            <div className="mb-3">
              <TurnstileWidget
                siteKey={cfg.turnstileSiteKey}
                onToken={(t) => {
                  setCaptcha(t);
                  if (t) setCaptchaError(null);
                }}
                onError={setCaptchaError}
              />
              {captchaError && <p className="mt-1.5 text-center text-xs text-critical-ink">{captchaError}</p>}
            </div>
          )}
          <a
            href={loginReady ? loginHref : undefined}
            onClick={(e) => {
              if (!loginReady) {
                e.preventDefault();
                return;
              }
              rememberReturn();
            }}
            aria-disabled={!loginReady}
            className={
              "inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-[#5865F2] text-[13px] font-medium text-white transition-[filter,opacity] " +
              (loginReady ? "hover:brightness-110" : "cursor-not-allowed opacity-50")
            }
          >
            <DiscordMark />
            {loginReady ? "Đăng nhập bằng Discord" : "Đang chờ xác minh…"}
          </a>
          {cfg.devLogin && (
            <div className="mt-4 border-t border-line pt-4">
              <Button
                className="w-full"
                icon={<KeyRound className="size-4" />}
                loading={devBusy}
                onClick={async () => {
                  setDevBusy(true);
                  try {
                    signIn(await authApi.devToken());
                    navigate(next, { replace: true });
                  } catch (e) {
                    toast.error(`Không lấy được token dev: ${errorMessage(e)}`);
                  } finally {
                    setDevBusy(false);
                  }
                }}
              >
                Đăng nhập dev (DEV_MODE)
              </Button>
              <p className="mt-2 text-xs text-ink-3">Chỉ hoạt động khi Master bật DEV_MODE. Không dùng ở production.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
